import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Receipt } from 'lucide-react';
import TopMenu from '../../components/TopMenu';
import StatusBadge from '../../components/MockInterview/StatusBadge';
import { useNotification } from '../../notification/NotificationContext';
import { getPaymentHistory } from '../../services/mockInterviewService';
import '../../components/MockInterview/MockInterview.css';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  } catch {
    return iso;
  }
};

// Backend returns rupees in some shapes (`amountInr`), paise in others
// (`amountPaise`). Render whichever exists.
const fmtAmount = (p) => {
  if (typeof p?.amountInr === 'number') return `₹${p.amountInr.toFixed(0)}`;
  if (typeof p?.amountPaise === 'number') return `₹${(p.amountPaise / 100).toFixed(0)}`;
  if (typeof p?.amount === 'number') return `₹${p.amount.toFixed(0)}`;
  return '—';
};

const PaymentHistoryPage = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const dark = theme === 'dark';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPaymentHistory()
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch((e) => showNotification(e.message || 'Failed to load payment history', 'error'))
      .finally(() => setLoading(false));
  }, [showNotification]);

  if (loading) {
    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="mi-loading"><div className="mi-spin" /></div>
      </div>
    );
  }

  return (
    <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="mi-container">
        <button className="mi-btn mi-btn-sm" onClick={() => navigate('/dashboard')} style={{ marginBottom: 14 }}>
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Back
        </button>
        <p className="mi-kicker">
          <Receipt size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Billing
        </p>
        <h1 className="mi-title">Payment History</h1>
        <p className="mi-subtitle">All transactions on your SkillOn subscription.</p>

        {items.length === 0 ? (
          <div className="mi-notice" style={{ marginTop: 18 }}>
            <h4>No payments yet</h4>
            <p>You haven't subscribed to any paid plan. Upgrade anytime to unlock unlimited sessions.</p>
          </div>
        ) : (
          <div className="mi-table-wrap">
            <table className="mi-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Billing</th>
                  <th>Amount (₹)</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id || p.razorpayPaymentId}>
                    <td>{p.planDisplayName || p.planKey || '—'}</td>
                    <td>{p.billingCycle || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{fmtAmount(p)}</td>
                    <td><StatusBadge status={p.status || 'Completed'} /></td>
                    <td>{fmtDate(p.createdAt || p.paidAt)}</td>
                    <td style={{ fontSize: 12, opacity: 0.7, fontFamily: 'monospace' }}>
                      {p.razorpayPaymentId || p.orderId || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistoryPage;
